import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, MailCheck } from "lucide-react";
import { api, apiErrorMessage, unwrap } from "@/lib/api";
import { Notice, Spinner } from "@/components/ui";
import { AuthShell, ErrorBox, Field } from "./LoginPage";

// =============================================================================
// Forgetting a password, resetting it, and confirming an email address.
//
// Three small screens kept together because they are one story: all three are
// reached from a link in an email or a link on the sign-in page, none of them
// has a session, and all three share AuthShell so the person never feels like
// they have been dropped somewhere else mid-flow.
// =============================================================================

/**
 * Asks for the reset link.
 *
 * Reports success whatever the answer, matching the API. Telling someone "no
 * account with that address" turns this box into a way to test a list of
 * emails against the user table, one at a time, for free.
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await unwrap(api.post("/auth/forgot-password", { email }));
      setSent(true);
    } catch (err) {
      setError(apiErrorMessage(err, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthShell title={t("auth.checkInbox")} subtitle={t("auth.resetSentSub")}>
        <Notice tone="success">
          <MailCheck size={16} className="mr-2 inline" />
          {t("auth.resetSent", { email })}
        </Notice>
        <p className="mt-6 text-center text-sm text-muted">
          <Link to="/login" className="font-medium text-primary-700 hover:underline">
            {t("auth.backToSignIn")}
          </Link>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t("auth.forgotTitle")} subtitle={t("auth.forgotSubtitle")}>
      <form onSubmit={submit} className="space-y-4">
        <Field
          label={t("auth.email")}
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          required
        />
        {error ? <ErrorBox>{error}</ErrorBox> : null}
        <button type="submit" disabled={busy} className="btn-primary w-full">
          {busy ? <Spinner /> : null}
          {t("auth.sendResetLink")}
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-muted">
        <Link to="/login" className="font-medium text-primary-700 hover:underline">
          {t("auth.backToSignIn")}
        </Link>
      </p>
    </AuthShell>
  );
}

/**
 * Sets the new password.
 *
 * Asks twice. The field can be revealed, so a confirmation is not strictly
 * needed — but this is the one password someone types while locked out and
 * cannot check afterwards without being locked out again, and the cost of the
 * second box is four seconds.
 *
 * The comparison happens before the request, so a mismatch does not burn the
 * token: the link is single use, and spending it on a typo would mean asking
 * for a whole new email.
 */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError(t("auth.passwordsDiffer"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await unwrap(api.post("/auth/reset-password", { token, password }));
      setDone(true);
      // A moment to read the confirmation before the sign-in page replaces it.
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(apiErrorMessage(err, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthShell title={t("auth.resetTitle")} subtitle={t("auth.resetSubtitle")}>
        <ErrorBox>{t("auth.linkMissing")}</ErrorBox>
        <p className="mt-6 text-center text-sm text-muted">
          <Link to="/forgot-password" className="font-medium text-primary-700 hover:underline">
            {t("auth.requestNewLink")}
          </Link>
        </p>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell title={t("auth.resetDoneTitle")} subtitle={t("auth.resetDoneSub")}>
        <Notice tone="success">
          <CheckCircle2 size={16} className="mr-2 inline" />
          {t("auth.resetDone")}
        </Notice>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t("auth.resetTitle")} subtitle={t("auth.resetSubtitle")}>
      <form onSubmit={submit} className="space-y-4">
        <Field
          label={t("auth.newPassword")}
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          minLength={8}
          required
          hint={t("auth.passwordHint")}
        />
        <Field
          label={t("auth.confirmPassword")}
          type="password"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          minLength={8}
          required
        />
        {error ? <ErrorBox>{error}</ErrorBox> : null}
        <button type="submit" disabled={busy} className="btn-primary w-full">
          {busy ? <Spinner /> : null}
          {t("auth.setPassword")}
        </button>
      </form>
    </AuthShell>
  );
}

/**
 * Confirms an address from the emailed link.
 *
 * Fires once on mount, which is the whole screen: there is nothing to ask the
 * person, and a "click here to confirm" button on a page they reached by
 * clicking a link to confirm is a second click for nothing.
 *
 * The guard matters though. React 18 runs effects twice in development, and
 * without it the second run presents a token the first run already burned —
 * which looks exactly like a broken link.
 */
export function VerifyEmailPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [state, setState] = useState<"working" | "done" | "failed">("working");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setState("failed");
      setError(t("auth.linkMissing"));
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        await unwrap(api.post("/auth/verify-email", { token }));
        if (!cancelled) setState("done");
      } catch (err) {
        if (!cancelled) {
          setError(apiErrorMessage(err, t("errors.generic")));
          setState("failed");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  return (
    <AuthShell title={t("auth.verifyTitle")} subtitle={t("auth.verifySubtitle")}>
      {state === "working" ? (
        <p className="flex items-center gap-2 text-sm text-muted">
          <Spinner /> {t("auth.verifying")}
        </p>
      ) : null}

      {state === "done" ? (
        <Notice tone="success">
          <CheckCircle2 size={16} className="mr-2 inline" />
          {t("auth.verified")}
        </Notice>
      ) : null}

      {state === "failed" ? <ErrorBox>{error}</ErrorBox> : null}

      <p className="mt-6 text-center text-sm text-muted">
        <Link to="/" className="font-medium text-primary-700 hover:underline">
          {t("auth.goToDashboard")}
        </Link>
      </p>
    </AuthShell>
  );
}
